import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/firebase/config';
import { setUserOnline, setUserOffline, touchUserPresence } from '@/firebase/rtdb';
import type { AppUser, Role, Permission, Company } from '@/types';
import { getRoleById, ALL_PERMISSIONS } from '@/services/roleService';
import { getUserById } from '@/services/userService';

import { logout } from '@/firebase/auth';
import { userRef, companyRef, snapshotToItem, onValue, off } from '@/firebase/database';
import toast from 'react-hot-toast';

interface AuthContextValue {
  firebaseUser: User | null;
  appUser: AppUser | null;
  company: Company | null;
  role: Role | null;
  permissions: Permission[];
  isAdmin: boolean;
  can: (permission: Permission) => boolean;
  canAny: (perms: Permission[]) => boolean;
  loading: boolean;
  companyId: string | null;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  firebaseUser: null,
  appUser: null,
  company: null,
  role: null,
  permissions: [],
  isAdmin: false,
  can: () => false,
  canAny: () => false,
  loading: true,
  companyId: null,
  refreshUser: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAppUser = async (user: User) => {
    try {
      const data = await getUserById(user.uid, user.email || undefined);
      if (data) {
        // 🚫 Block deactivated users
        if (data.status === 'inactive') {
          await setUserOffline(data.companyId, user.uid);
          await logout();
          setAppUser(null);
          setRole(null);
          setPermissions([]);
          toast.error('Your account has been deactivated. Please contact your organization administrator.');
          return;
        }

        setAppUser(data);
        const roleData = await getRoleById(data.companyId, data.roleId || 'Staff');
        if (roleData) {
          setRole(roleData);
          setPermissions(roleData.permissions || []);
        } else {
          // Default fallback
          const isMasterAdmin = data.uid === data.companyId || data.roleId === 'Admin';
          setRole({
            id: data.roleId || (isMasterAdmin ? 'Admin' : 'Staff'),
            companyId: data.companyId,
            name: data.roleId || (isMasterAdmin ? 'Admin' : 'Staff'),
            description: `${data.roleId || (isMasterAdmin ? 'Admin' : 'Staff')} Role`,
            permissions: isMasterAdmin ? ALL_PERMISSIONS : [],
            isSystem: true,
            createdAt: null as any,
            updatedAt: null as any,
          });
          setPermissions(isMasterAdmin ? ALL_PERMISSIONS : []);
        }
        await setUserOnline(data.companyId, user.uid, data.name);
      }
    } catch (err) {
      console.error('Failed to load app user:', err);
    }
  };

  const refreshUser = async () => {
    if (auth.currentUser) {
      try {
        await auth.currentUser.reload();
        setFirebaseUser(
          Object.assign(
            Object.create(Object.getPrototypeOf(auth.currentUser)),
            auth.currentUser,
          ),
        );
        await loadAppUser(auth.currentUser);
      } catch (err) {
        console.error('Failed to refresh user:', err);
      }
    }
  };

  // Listen to auth state and live profile status changes
  useEffect(() => {
    let userUnsubscribe: (() => void) | null = null;

    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);
      if (user) {
        await loadAppUser(user);

        // Real-time listener for user profile and status changes
        const uRef = userRef(user.uid);
        userUnsubscribe = onValue(uRef, async (snap) => {
          if (snap.exists()) {
            const val = snap.val();
            if (val && val.status === 'inactive') {
              await setUserOffline(val.companyId || '', user.uid);
              await logout();
              setAppUser(null);
              setRole(null);
              setPermissions([]);
              toast.error('Your account has been deactivated by the organization administrator.');
              return;
            }
            if (val) {
              setAppUser((prev) => ({ ...(prev || {}), ...val, uid: user.uid } as AppUser));
            }
          }
        });
      } else {
        if (userUnsubscribe) {
          userUnsubscribe();
          userUnsubscribe = null;
        }
        setAppUser(null);
        setRole(null);
        setPermissions([]);
      }
      setLoading(false);
    });

    return () => {
      authUnsubscribe();
      if (userUnsubscribe) userUnsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!appUser || !appUser.companyId || !appUser.uid) return;

    // Heartbeat every 45s to keep active presence
    const interval = setInterval(() => {
      touchUserPresence(appUser.companyId, appUser.uid, appUser.name);
    }, 45000);

    // Refresh presence on user focus / visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        touchUserPresence(appUser.companyId, appUser.uid, appUser.name);
      }
    };

    const handleBeforeUnload = async () => {
      await setUserOffline(appUser.companyId, appUser.uid);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [appUser]);

  const isAdmin =
    role?.name?.toLowerCase() === 'admin' ||
    (appUser?.uid && appUser?.companyId && appUser.uid === appUser.companyId);

  const can = (perm: Permission): boolean => {
    if (isAdmin) return true;
    return permissions.includes(perm);
  };

  const canAny = (perms: Permission[]): boolean => {
    if (isAdmin) return true;
    return perms.some((p) => permissions.includes(p));
  };

  // Real-time listener for company details (e.g. company name, logo)
  useEffect(() => {
    if (!appUser?.companyId) {
      setCompany(null);
      return;
    }

    const cRef = companyRef(appUser.companyId);
    const unsubscribe = onValue(cRef, (snap) => {
      if (snap.exists()) {
        const val = snap.val();
        setCompany({ id: appUser.companyId, ...val } as Company);
      } else {
        setCompany(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [appUser?.companyId]);

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        appUser,
        company,
        role,
        permissions,
        isAdmin,
        can,
        canAny,
        loading,
        companyId: appUser?.companyId ?? null,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
